import { Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import {
    MaiaWizardStep,
    MaiaWizardProgressUpdate,
    SovActiveJobSummary,
    SovJobState,
} from "../../model/maia/maia-wizard-progress";
import { SUPPRESS_ERROR_NOTIFICATION } from "../../core/interceptors/http-error-interceptor.service";
import {
    SovAiField,
    SovComputedReport,
    SovJobEvent,
    SovLastJob,
    SovOverlayProgress,
    SovPaidOverlayOption,
    SovReportAggregate,
    SovReportDefinition,
    SovReportExportSection,
    SovReportField,
    SovReportOverview,
    SovReportTopLocations,
    SovUploadResponse,
    SovUploadStatus,
    SovUploadSummary,
} from "../../model/maia/sov-upload";
import { SSE_ENABLED, SseClientService, SseEvent } from "../../core/sse";

/**
 * MAIA SOV upload.
 *
 * Follows the same shape as the other services in this layer: relative URLs,
 * with HttpDomainInterceptor prefixing the API base domain.
 */
@Injectable({
    providedIn: "root",
})
export class MaiaUploadService {
    /** Live parse progress for a job. */
    static readonly JOB_EVENTS_URL = (jobId: number) => `/sov/${jobId}/events`;

    constructor(
        private readonly http: HttpClient,
        private readonly sse: SseClientService
    ) {}

    /** Uploads a single SOV file for the given dataset. */
    uploadSovFile(datasetId: string, file: File): Observable<SovUploadResponse> {
        const formData = new FormData();
        formData.append("file", file);

        return this.http.post<SovUploadResponse>(
            `/sov/dataset/${datasetId}/upload`,
            formData
        );
    }

    /** Count of AI-draft locations for a dataset's onboarding (job-scoped when jobId is given). */
    getDraftLocationCount(datasetId: string, jobId?: number | null): Observable<number> {
        const url =
            jobId != null
                ? `/sov/drafts/dataset/${datasetId}/count?jobId=${jobId}`
                : `/sov/drafts/dataset/${datasetId}/count`;
        return this.http
            .get<{ count: number }>(url)
            .pipe(map((response) => (response ? response.count : 0)));
    }

    /** Streams the paid-overlay stage's progress; completes on the terminal "done" frame. */
    streamPaidOverlayEvents(jobId: number): Observable<SseEvent<SovJobEvent>> {
        return this.sse.streamUntil<SovJobEvent>(MaiaUploadService.JOB_EVENTS_URL(jobId), {
            maxRetries: 2,
            isComplete: (event) => event.event === "done",
            isFailure: (event) => event.event === "error",
        });
    }

    /**
     * Overlay completion (free + paid) from per-row status - the reliable "is enrichment done"
     * signal for the Enhancing screen (job.status is unreliable when both stages run concurrently).
     */
    getOverlayProgress(datasetId: string, jobId: number): Observable<SovOverlayProgress> {
        return this.http.get<SovOverlayProgress>(
            `/sov/${jobId}/overlay-progress?datasetId=${encodeURIComponent(datasetId)}`
        );
    }

    /** Paid overlays assigned to the dataset's account - the only ones the enrich step may offer. */
    getPaidOverlays(datasetId: string): Observable<SovPaidOverlayOption[]> {
        return this.http.get<SovPaidOverlayOption[]>(
            `/sov/paid-overlays?datasetId=${encodeURIComponent(datasetId)}`
        );
    }

    /**
     * The dataset's most recent SOV job, or null when it has never had one.
     *
     * A dataset may only have one job in flight at a time, so this is what
     * decides whether "Ask MAIA" starts a new run or resumes the open one.
     * A 404 is treated as "no job yet" rather than an error.
     */
    getLastJob(
        accountId: string | number,
        datasetId: string
    ): Observable<SovLastJob | null> {
        return this.http
            .get<SovLastJob>(`/sov/account/${accountId}/dataset/${datasetId}/last-job`)
            .pipe(catchError(() => of(null)));
    }

    /**
     * One-shot read of a job's current terminal event frame.
     *
     * Opens a fresh connection to the job's event stream and takes the first
     * terminal frame, which for an already-finished job the server sends
     * immediately on connect and then closes. That snapshot is the only place
     * `duplicateRowCount` appears — the live `done` frame emitted as parsing
     * ends does not carry it — so a screen that needs the duplicate count must
     * ask again here rather than read the stream it was already watching.
     *
     * Falls back to the job's summary document, which holds the same counters,
     * when SSE is switched off or the connection fails. Either way the caller
     * gets one object, never an error.
     */
    getJobEventSnapshot(jobId: number): Observable<SovJobEvent> {
        if (!SSE_ENABLED) {
            return this.getSnapshotFromSummary(jobId);
        }

        return this.sse
            .streamJson<SovJobEvent>(MaiaUploadService.JOB_EVENTS_URL(jobId), {
                events: ["done", "error"],
                maxRetries: 0,
            })
            .pipe(
                // Unsubscribing aborts the request, so this also closes the
                // connection rather than leaving it open behind the screen.
                take(1),
                map((event) => event.data || {}),
                catchError(() => this.getSnapshotFromSummary(jobId))
            );
    }

    /** The job's full counter summary document (totals, geocode outcomes, duplicates). */
    getUploadSummary(jobId: number): Observable<SovUploadSummary> {
        return this.http.get<SovUploadSummary>(`/sov/upload/${jobId}/summary`);
    }

    /** The same counters as a terminal event frame, read from the summary document. */
    private getSnapshotFromSummary(jobId: number): Observable<SovJobEvent> {
        return this.getUploadSummary(jobId).pipe(
            map((summary) => ({
                jobId,
                totalRows: summary ? summary.totalRecords : undefined,
                duplicateRowCount: summary ? summary.duplicateRowCount : undefined,
            })),
            catchError(() => of({ jobId } as SovJobEvent))
        );
    }

    /**
     * Downloads the duplicate-review workbook: the file exactly as uploaded,
     * with the duplicate-flagged rows sorted to the top and highlighted, so
     * the user can fix them and bring the file back.
     *
     * `observe: "response"` so the caller can take the filename from
     * Content-Disposition instead of hard-coding one.
     */
    downloadDuplicateReview(jobId: number): Observable<HttpResponse<Blob>> {
        return this.http.get(`/sov/${jobId}/duplicates/download`, {
            observe: "response",
            responseType: "blob",
        });
    }

    /**
     * Sends a corrected duplicate-review workbook back.
     *
     * The backend merges it per row by id rather than reparsing the file, so
     * row counts can change: a row left out of the re-uploaded file is removed
     * from the job, and a row with no id is added. Callers therefore re-read
     * the job's totals afterwards instead of keeping the ones they held.
     */
    importDuplicateReview(jobId: number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append("file", file);

        return this.http.post(`/sov/${jobId}/duplicates/import`, formData);
    }

    /**
     * Filename for a downloaded workbook.
     *
     * Prefers the server's Content-Disposition, which is only readable
     * cross-origin when the server sends
     * `Access-Control-Expose-Headers: Content-Disposition` — hence the
     * fallback rather than a file called "download".
     */
    resolveDownloadFileName(response: HttpResponse<Blob>, fallback: string): string {
        const disposition = response.headers.get("Content-Disposition") || "";

        // RFC 5987 form first: filename*=UTF-8''name.xlsx
        const encoded = disposition.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
        if (encoded && encoded[1]) {
            try {
                return decodeURIComponent(encoded[1]);
            } catch {
                return encoded[1];
            }
        }

        const plain = disposition.match(/filename="?([^";]+)"?/i);
        if (plain && plain[1]) {
            return plain[1].trim();
        }

        return fallback;
    }

    /**
     * Downloads the blank SOV template — the sample workbook offered from the
     * upload guidelines panel.
     *
     * `observe: "response"` so the caller can take the filename from
     * Content-Disposition instead of hard-coding one.
     */
    downloadTemplate(): Observable<HttpResponse<Blob>> {
        return this.http.get(`/sov/template`, {
            observe: "response",
            responseType: "blob",
        });
    }

    /** Downloads the enriched portfolio file for a job. */
    downloadEnriched(jobId: number): Observable<Blob> {
        return this.http.get(`/sov/${jobId}/download`, { responseType: "blob" });
    }

    /** Generates the report overview export (xlsx or pdf) for a job, using the on-screen section config. */
    exportReport(
        datasetId: string,
        jobId: number,
        format: "xlsx" | "pdf",
        title: string,
        measure: string,
        sections: SovReportExportSection[] = []
    ): Observable<Blob> {
        const params = new HttpParams().set("datasetId", datasetId).set("format", format);
        const body = { title, topMeasure: measure, sections };
        return this.http.post(`/sov/${jobId}/report/export`, body, { params, responseType: "blob" });
    }

    /** The auto-built Overview report definition for a job (which sections the data supports). */
    getReportDefinition(datasetId: string, jobId: number, title: string): Observable<SovReportDefinition> {
        const params = new HttpParams().set("datasetId", datasetId).set("title", title);
        return this.http.get<SovReportDefinition>(`/sov/${jobId}/report/definition`, { params });
    }

    /** Computes a (tailored) definition into tables for the screen - one pass over the job's rows. */
    computeReport(datasetId: string, jobId: number, definition: SovReportDefinition): Observable<SovComputedReport> {
        const params = new HttpParams().set("datasetId", datasetId);
        return this.http.post<SovComputedReport>(`/sov/${jobId}/report/compute`, definition, { params });
    }

    /** Builds and downloads the Overview xlsx workbook from a (tailored) definition. */
    exportOverview(datasetId: string, jobId: number, definition: SovReportDefinition): Observable<Blob> {
        const params = new HttpParams().set("datasetId", datasetId);
        return this.http.post(`/sov/${jobId}/report/overview-export`, definition, { params, responseType: "blob" });
    }

    /** File-overview KPIs for the report, computed from the job's SOV rows. */
    getReportOverview(datasetId: string, jobId: number): Observable<SovReportOverview> {
        return this.http.get<SovReportOverview>(
            `/sov/${jobId}/report/overview?datasetId=${encodeURIComponent(datasetId)}`
        );
    }

    /** Taxonomy field catalog for the report config dropdowns (numeric = measure, else dimension). */
    getTaxonomyFields(): Observable<SovReportField[]> {
        return this.http.get<SovReportField[]>(`/sov/report/taxonomy-fields`);
    }

    /** The job file's own columns (name, numeric, % populated) for the Report value dropdown. */
    getReportFields(datasetId: string, jobId: number): Observable<SovReportField[]> {
        const params = new HttpParams().set("datasetId", datasetId);
        return this.http.get<SovReportField[]>(`/sov/${jobId}/report/fields`, { params });
    }

    /** A report chart section aggregation (group-by dimension, sum/avg/count measure). */
    getReportAggregate(
        datasetId: string,
        jobId: number,
        body: { dimension: string; measure: string; aggregation: string }
    ): Observable<SovReportAggregate> {
        return this.http.post<SovReportAggregate>(
            `/sov/${jobId}/report/aggregate?datasetId=${encodeURIComponent(datasetId)}`,
            body
        );
    }

    /** "Top locations" report section — highest-value locations ranked by a measure. */
    getTopLocations(datasetId: string, jobId: number, limit = 10, measure = ""): Observable<SovReportTopLocations> {
        const m = measure ? `&measure=${encodeURIComponent(measure)}` : "";
        return this.http.get<SovReportTopLocations>(
            `/sov/${jobId}/report/top-locations?datasetId=${encodeURIComponent(datasetId)}&limit=${limit}${m}`
        );
    }

    /** Thematic-links the job's AI drafts before grouping (empty filter = all AI drafts). */
    triggerThematicLink(datasetId: string): Observable<unknown> {
        return this.http.post(`/sov/${datasetId}/thematic-link`, {});
    }

    /** Triggers the free-overlay (core MAPTYCS) stage as a background run. */
    triggerFreeOverlay(datasetId: string, jobId: number): Observable<unknown> {
        const params = new HttpParams().set("datasetId", datasetId);
        return this.http.post(`/sov/${jobId}/overlay`, null, { params });
    }

    /** Triggers the paid-overlay stage for the selected overlay datasets (background run). */
    triggerPaidOverlay(
        datasetId: string,
        jobId: number,
        overlayDatasetIds: string[]
    ): Observable<unknown> {
        let params = new HttpParams().set("datasetId", datasetId);
        overlayDatasetIds.forEach((id) => (params = params.append("overlayDatasetIds", id)));
        return this.http.post(`/sov/${jobId}/paid-overlay`, null, { params });
    }

    /** Columns offered in the grouping filter's Field dropdown. */
    getAiFields(datasetId: string): Observable<SovAiField[]> {
        return this.http.get<SovAiField[]>(`/sov/dataset/${datasetId}/ai-fields`);
    }

    /** Distinct values of a field among the job's AI drafts - the facet checklist options. */
    getFieldValues(datasetId: string, fieldId: string, jobId?: number | null): Observable<string[]> {
        const job = jobId != null ? `&jobId=${jobId}` : "";
        return this.http.get<string[]>(
            `/sov/drafts/dataset/${datasetId}/field-values?fieldId=${encodeURIComponent(fieldId)}${job}`
        );
    }

    /** Numeric min/max of a field among the job's AI drafts - the range filter's bounds. */
    getFieldRange(
        datasetId: string,
        fieldId: string,
        jobId?: number | null
    ): Observable<{ min: number | null; max: number | null }> {
        const job = jobId != null ? `&jobId=${jobId}` : "";
        return this.http.get<{ min: number | null; max: number | null }>(
            `/sov/drafts/dataset/${datasetId}/field-range?fieldId=${encodeURIComponent(fieldId)}${job}`
        );
    }

    /** Approve (promote) the AI drafts matching the criteria into the real dataset under a group. */
    approve(
        datasetId: string,
        jobId: number | null,
        toGroupId: number,
        criteria: Array<{ fieldId: string; values: string[]; min: number | null; max: number | null }>
    ): Observable<{ jobId: number | null; selected: number; approved: number }> {
        return this.http.post<{ jobId: number | null; selected: number; approved: number }>(
            `/sov/${datasetId}/approve`,
            { jobId, toGroupId, criteria }
        );
    }

    /** How many AI drafts match the ticked facet criteria (values for text, min/max range for numbers). */
    getMatchCount(
        datasetId: string,
        jobId: number | null,
        criteria: Array<{ fieldId: string; values: string[]; min: number | null; max: number | null }>
    ): Observable<number> {
        return this.http
            .post<{ count: number }>(`/sov/drafts/dataset/${datasetId}/match-count`, { jobId, criteria })
            .pipe(map((response) => (response ? response.count : 0)));
    }

    /**
     * Full job state, including wizard-progress fields (`currentStep`,
     * `currentScreen`, `stepStatuses`) — the resume-on-load call.
     */
    /**
     * Closes a job, releasing its dataset's single active-job slot.
     *
     * A dataset allows one active job at a time, so an abandoned job has to be
     * closed before a new upload will be accepted. Closing also removes that
     * job's unapproved AI drafts, which nothing else cleans up once it is
     * abandoned; anything already approved into the live dataset is untouched,
     * and its staging rows are left to their normal expiry so its report and
     * downloads stay reachable.
     *
     * Idempotent. Refused with 409 while a background stage is still running
     * on the job, which is a real "try again shortly", not a dead end.
     */
    closeJob(jobId: number): Observable<SovJobState> {
        return this.http.post<SovJobState>(`/sov/${jobId}/close`, {});
    }

    /** The caller reports a failure itself (see MaiaPageComponent.resumeJob), so no generic toast. */
    getJobState(jobId: number): Observable<SovJobState> {
        return this.http.get<SovJobState>(`/sov/${jobId}`, {
            context: new HttpContext().set(SUPPRESS_ERROR_NOTIFICATION, true),
        });
    }

    /**
     * The caller's own open jobs on a dataset - 0 or 1 since JIRA-SOV-013, except for leftover
     * pre-SOV-013 jobs. Other users' jobs are never listed.
     */
    getActiveJobs(datasetId: string): Observable<SovActiveJobSummary[]> {
        return this.http
            .get<SovActiveJobSummary[]>(`/sov/dataset/${datasetId}/active-jobs`, {
                context: new HttpContext().set(SUPPRESS_ERROR_NOTIFICATION, true),
            })
            .pipe(map((jobs) => jobs || []));
    }

    /**
     * Records which wizard screen the job is currently on. Fire-and-forget —
     * callers don't need to block the UI on this. `completedSteps` is
     * additive server-side: send only the step(s) that just finished on
     * this call, never the full history.
     */
    updateWizardProgress(
        jobId: number,
        currentScreen: string,
        completedSteps: MaiaWizardStep[] = []
    ): Observable<void> {
        const body: MaiaWizardProgressUpdate = { currentScreen, completedSteps };
        return this.http.put<void>(`/sov/${jobId}/wizard-progress`, body);
    }

    /**
     * Streams parse progress for a job, completing on the terminal frame.
     *
     * Emits `SovJobEvent`; the observable completes once the job reaches
     * PARSED and errors (with an `SseStreamError`) once it reaches FAILED, so
     * callers need no explicit teardown on either path.
     */
    streamJobEvents(jobId: number): Observable<SseEvent<SovJobEvent>> {
        return this.sse.streamUntil<SovJobEvent>(
            MaiaUploadService.JOB_EVENTS_URL(jobId),
            {
                maxRetries: 2,
                // COMPLETE ends the stream just as PARSED does. Without it the
                // frame reads as ordinary progress, the clean server close looks
                // like a drop, and the client reconnects twice before giving up.
                isComplete: (event) =>
                    this.statusOf(event) === SovUploadStatus.PARSED ||
                    this.statusOf(event) === SovUploadStatus.COMPLETE,
                isFailure: (event) => this.statusOf(event) === SovUploadStatus.FAILED,
            }
        );
    }

    /**
     * Status of a frame — from the payload, falling back to the SSE `event:`
     * name so a stream that only labels its frames still drives the UI.
     */
    private statusOf(event: SseEvent<SovJobEvent>): SovUploadStatus | null {
        if (event.data && event.data.status) {
            return event.data.status;
        }
        switch (event.event) {
            case "done":
                return SovUploadStatus.PARSED;
            case "error":
                return SovUploadStatus.FAILED;
            default:
                return null;
        }
    }
}
